/**
 * Session Lobby Component
 * Waiting room for group sessions before the adventure starts.
 * Two-column layout on desktop: left panel (participants + settings + actions), right panel (chat).
 * Stacks vertically on mobile.
 */

import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Avatar,
  Chip,
  Tooltip,
  Badge,
  LinearProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as StartIcon,
  ExitToApp as LeaveIcon,
  PersonAdd as InviteIcon,
  CheckCircle as ReadyIcon,
  Cancel as UnreadyIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
  HourglassEmpty as WaitingIcon,
  Forum as ChatIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '@api/endpoints/chat'
import { sessionsApi } from '@api/endpoints/sessions'
import { useChatStore } from '@store/chatStore'
import { useAuthStore } from '@store/authStore'
import { useGeneralChatConnection } from '@hooks/useSignalR'
import { MessageThread } from '@features/chat/components/MessageThread'
import { MessageInput } from '@features/chat/components/MessageInput'
import { useSessionStore } from '../../stores/sessionStore'
import {
  useMarkReady,
  useStartSession,
  useLeaveSession,
} from '../../hooks/useSession'
import { useSessionParticipants } from '../../hooks/useSessionInvites'
import { SessionParticipantRole } from '@api/types/session'
import SessionInvitePanel from './SessionInvitePanel'
import SessionSettingsPanel from './SessionSettingsPanel'

interface SessionLobbyProps {
  sessionId: string
  adventureTitle: string
  isOwner: boolean
  conversationId: string | null
  onStarted: () => void
  onLeft: () => void
}

export default function SessionLobby({
  sessionId,
  adventureTitle,
  isOwner,
  conversationId,
  onStarted,
  onLeft,
}: SessionLobbyProps) {
  const store = useSessionStore()
  const user = useAuthStore((s) => s.user)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [invitePanelOpen, setInvitePanelOpen] = useState(false)
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [startErrors, setStartErrors] = useState<string[]>([])

  // Participants
  const { data: participants } = useSessionParticipants(sessionId)

  useEffect(() => {
    if (participants && Array.isArray(participants)) {
      store.setParticipants(participants)
    }
  }, [participants])

  // Detect owner from participants
  useEffect(() => {
    if (!user?.userId || !store.participants.length) return
    const self = store.participants.find(p => p.userId === user.userId)
    if (self) {
      store.setIsSessionOwner(self.role === SessionParticipantRole.Owner)
    }
  }, [store.participants, user?.userId])

  // Ready state
  const selfParticipant = store.participants.find(p => p.userId === user?.userId)
  const isReady = selfParticipant?.isReady ?? false
  const readyCount = store.participants.filter(p => p.isReady).length
  const totalCount = store.participants.length
  const readyPercent = totalCount > 0 ? (readyCount / totalCount) * 100 : 0

  // Mutations
  const markReady = useMarkReady()
  const startSession = useStartSession()
  const leaveSession = useLeaveSession()

  const handleToggleReady = () => {
    markReady.mutate({ sessionId, ready: !isReady })
  }

  const handleStartSession = async () => {
    setStartErrors([])
    try {
      const validation = await sessionsApi.validateSessionStart(sessionId)
      if (!validation.canStart) {
        setStartErrors(validation.errors)
        return
      }
      await startSession.mutateAsync(sessionId)
      onStarted()
    } catch (err: any) {
      // Extract validation errors from 400 responses
      const responseErrors = err?.response?.data?.errors
      if (Array.isArray(responseErrors) && responseErrors.length > 0) {
        setStartErrors(responseErrors)
      } else {
        setStartErrors([err?.response?.data?.message || err?.message || 'Failed to start session'])
      }
    }
  }

  const handleLeaveSession = async () => {
    setLeaveConfirmOpen(false)
    try {
      await leaveSession.mutateAsync(sessionId)
      onLeft()
    } catch {
      // Error handled by mutation
    }
  }

  // ========================
  // Chat
  // ========================

  const { setMessages, addMessage, markConversationRead, suppressPopout, unsuppressPopout } = useChatStore()

  // Suppress chat popout for the lobby's conversation while this component is mounted
  useEffect(() => {
    if (!conversationId) return
    suppressPopout(conversationId)
    return () => { unsuppressPopout(conversationId) }
  }, [conversationId, suppressPopout, unsuppressPopout])
  const {
    isConnected,
    joinConversation,
    leaveConversation,
    sendTypingIndicator,
    onNewMessage,
    onTypingIndicator,
    onConversationUpdateSignal,
  } = useGeneralChatConnection()
  const queryClient = useQueryClient()

  const [typingUsers, setTypingUsers] = useState<Map<string, { userName: string; timeout: ReturnType<typeof setTimeout> }>>(new Map())

  const { data: messagesData } = useQuery({
    queryKey: ['chatMessages', conversationId],
    queryFn: () => chatApi.getMessages(conversationId!, 1, 50),
    enabled: !!conversationId,
  })

  useEffect(() => {
    if (messagesData && conversationId) {
      const messages = Array.isArray(messagesData) ? [...messagesData].reverse() : []
      setMessages(conversationId, messages, 1, false)
    }
  }, [messagesData, conversationId, setMessages])

  useEffect(() => {
    if (!isConnected || !conversationId) return
    joinConversation(conversationId)
    chatApi.markAsRead(conversationId)
    markConversationRead(conversationId)
    return () => { leaveConversation(conversationId) }
  }, [isConnected, conversationId, joinConversation, leaveConversation, markConversationRead])

  useEffect(() => {
    if (!isConnected || !conversationId) return
    const unsub = onNewMessage((message) => {
      if (message.conversationId === conversationId) {
        addMessage(conversationId, message)
        // Refresh data when system messages arrive with metadata
        if (message.isSystemMessage && message.metadata) {
          try {
            const meta = JSON.parse(message.metadata)
            if (meta.Action === 'participant_joined' || meta.Action === 'participant_left') {
              queryClient.invalidateQueries({ queryKey: ['sessions', 'participants', sessionId] })
            } else if (meta.Action === 'settings_changed') {
              // Re-fetch session to get updated voting settings
              sessionsApi.getSession(sessionId).then(sessionState => {
                if (sessionState.votingSettings) {
                  useSessionStore.setState({ votingSettings: sessionState.votingSettings })
                }
              }).catch(err => console.error('Failed to refresh session settings:', err))
            }
          } catch { /* ignore parse errors */ }
        }
      }
    })
    return unsub
  }, [isConnected, conversationId, onNewMessage, addMessage, queryClient, sessionId])

  // Also listen for ConversationUpdateSignal (server may send this instead of ReceiveNewMessage)
  useEffect(() => {
    if (!isConnected || !conversationId) return

    const unsub = onConversationUpdateSignal?.((signal) => {
      if (signal.conversationId !== conversationId) return
      if (signal.senderId === user?.userId) return

      if (signal.updateType === 'NewMessage') {
        queryClient.invalidateQueries({ queryKey: ['chatMessages', conversationId] })
        chatApi.markAsRead(conversationId)
        markConversationRead(conversationId)
        // Refresh session settings & participants in case this was a system message
        // (ConversationUpdateSignal doesn't carry message content, so we refetch proactively)
        sessionsApi.getSession(sessionId).then(sessionState => {
          if (sessionState.votingSettings) {
            useSessionStore.setState({ votingSettings: sessionState.votingSettings })
          }
        }).catch(err => console.error('Failed to refresh session settings:', err))
        queryClient.invalidateQueries({ queryKey: ['sessions', 'participants', sessionId] })
      } else if (signal.updateType === 'MessagesRead' || signal.updateType === 'ReactionChanged' || signal.updateType === 'MessageDeleted') {
        queryClient.invalidateQueries({ queryKey: ['chatMessages', conversationId] })
      }
    })

    return () => { unsub?.() }
  }, [isConnected, onConversationUpdateSignal, conversationId, user?.userId, queryClient, markConversationRead])

  useEffect(() => {
    if (!isConnected || !conversationId) return
    const unsub = onTypingIndicator((indicator) => {
      if (indicator.conversationId !== conversationId || indicator.userId === user?.id) return
      setTypingUsers((prev) => {
        const newMap = new Map(prev)
        if (indicator.isTyping) {
          const existing = newMap.get(indicator.userId)
          if (existing) clearTimeout(existing.timeout)
          const timeout = setTimeout(() => {
            setTypingUsers((curr) => {
              const updated = new Map(curr)
              updated.delete(indicator.userId)
              return updated
            })
          }, 5000)
          newMap.set(indicator.userId, { userName: indicator.userName, timeout })
        } else {
          const existing = newMap.get(indicator.userId)
          if (existing) clearTimeout(existing.timeout)
          newMap.delete(indicator.userId)
        }
        return newMap
      })
    })
    return unsub
  }, [isConnected, conversationId, onTypingIndicator, user?.id])

  useEffect(() => {
    setTypingUsers((prev) => {
      prev.forEach(({ timeout }) => clearTimeout(timeout))
      return new Map()
    })
  }, [conversationId])

  const sendMessageMutation = useMutation({
    mutationFn: ({ content, imageId }: { content: string; imageId?: string }) =>
      chatApi.sendMessage(conversationId!, { content, imageId }),
    onSuccess: (message) => {
      addMessage(conversationId!, message)
    },
  })

  const handleSendMessage = (content: string, imageId?: string) => {
    if (!conversationId) return
    sendMessageMutation.mutate({ content, imageId })
  }

  const handleImageUpload = async (file: File): Promise<string | undefined> => {
    try {
      const response = await chatApi.uploadImage(file)
      return response.imageId
    } catch (error) {
      console.error('Failed to upload image:', error)
      throw error
    }
  }

  const handleTyping = (isTyping: boolean) => {
    if (!conversationId) return
    sendTypingIndicator(conversationId, isTyping)
  }

  const getStatusColor = (isOnline: boolean, lastSeenAt?: string) => {
    if (isOnline) return '#4caf50'
    if (lastSeenAt) {
      const lastSeen = new Date(lastSeenAt).getTime()
      if (lastSeen > Date.now() - 5 * 60 * 1000) return '#ff9800'
    }
    return '#9e9e9e'
  }

  // ========================
  // Render
  // ========================

  const memberList = store.participants

  // Shared participant avatar renderer (used in both mobile strip and desktop list)
  const renderMobileParticipants = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        overflowX: 'auto',
        flexShrink: 0,
        '&::-webkit-scrollbar': { height: 0 },
      }}
    >
      {/* Ready count + progress */}
      <Chip
        label={`${readyCount}/${totalCount}`}
        size="small"
        color={readyCount === totalCount ? 'success' : 'default'}
        variant={readyCount === totalCount ? 'filled' : 'outlined'}
        sx={{ fontSize: '0.7rem', height: 22, flexShrink: 0 }}
      />

      {/* Compact avatar strip */}
      {memberList.map((member) => (
        <Tooltip
          key={member.userId}
          title={`${member.userName}${member.role === 1 ? ' (Owner)' : ''}${member.isReady ? ' - Ready' : ''}`}
        >
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={member.avatarUrl}
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                border: member.role === 1 ? '2px solid' : member.userId === user?.userId ? '2px solid' : 'none',
                borderColor: member.role === 1 ? 'warning.main' : 'primary.main',
              }}
            >
              {member.userName?.[0]?.toUpperCase()}
            </Avatar>
            {/* Online dot */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -1,
                right: -1,
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: getStatusColor(member.isOnline, member.lastSeenAt),
                border: '2px solid',
                borderColor: 'background.paper',
              }}
            />
            {/* Ready checkmark */}
            {member.isReady && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -3,
                  right: -3,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1.5px solid',
                  borderColor: 'background.paper',
                }}
              >
                <CheckIcon sx={{ fontSize: 10, color: '#fff' }} />
              </Box>
            )}
          </Box>
        </Tooltip>
      ))}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Action buttons - compact */}
      <Button
        size="small"
        variant={isReady ? 'outlined' : 'contained'}
        color={isReady ? 'inherit' : 'success'}
        onClick={handleToggleReady}
        disabled={markReady.isPending}
        sx={{ flexShrink: 0, minWidth: 0, px: 1.5, fontSize: '0.75rem' }}
      >
        {isReady ? 'Unready' : 'Ready'}
      </Button>

      <Tooltip title="Invite players">
        <IconButton size="small" onClick={() => setInvitePanelOpen(true)}>
          <InviteIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Session settings">
        <IconButton size="small" onClick={() => setSettingsOpen(true)}>
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )

  const renderDesktopLeftPanel = () => (
    <Box
      sx={{
        width: 380,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: 1,
        borderColor: 'divider',
        overflow: 'auto',
      }}
    >
      {/* Participants section */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Players ({totalCount})
            </Typography>
          </Box>
          <Chip
            label={`${readyCount}/${totalCount} ready`}
            size="small"
            color={readyCount === totalCount ? 'success' : 'default'}
            variant={readyCount === totalCount ? 'filled' : 'outlined'}
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        </Box>

        {/* Ready progress bar */}
        <LinearProgress
          variant="determinate"
          value={readyPercent}
          color={readyCount === totalCount ? 'success' : 'warning'}
          sx={{ mb: 1.5, borderRadius: 1, height: 4 }}
        />

        {/* Participant list */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {memberList.map((member) => (
            <Box
              key={member.userId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                borderRadius: 1.5,
                bgcolor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(0,0,0,0.02)',
                border: member.userId === user?.userId ? '1px solid' : '1px solid transparent',
                borderColor: member.userId === user?.userId ? 'primary.main' : 'transparent',
              }}
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: getStatusColor(member.isOnline, member.lastSeenAt),
                      border: '2px solid',
                      borderColor: 'background.paper',
                    }}
                  />
                }
              >
                <Avatar
                  src={member.avatarUrl}
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: '0.85rem',
                    border: member.role === 1 ? '2px solid' : 'none',
                    borderColor: 'warning.main',
                  }}
                >
                  {member.userName?.[0]?.toUpperCase()}
                </Avatar>
              </Badge>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                  {member.userName}
                  {member.userId === user?.userId && (
                    <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 0.5 }}>
                      (you)
                    </Typography>
                  )}
                </Typography>
                {member.role === SessionParticipantRole.Owner && (
                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                    Owner
                  </Typography>
                )}
              </Box>

              {member.isReady ? (
                <Tooltip title="Ready">
                  <CheckIcon sx={{ fontSize: 20, color: 'success.main' }} />
                </Tooltip>
              ) : (
                <Tooltip title="Not ready">
                  <WaitingIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>

        {/* Ready toggle + Invite */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            fullWidth
            variant={isReady ? 'outlined' : 'contained'}
            color={isReady ? 'inherit' : 'success'}
            startIcon={isReady ? <UnreadyIcon /> : <ReadyIcon />}
            onClick={handleToggleReady}
            disabled={markReady.isPending}
            size="small"
          >
            {isReady ? 'Unready' : 'Ready Up'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InviteIcon />}
            onClick={() => setInvitePanelOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            Invite
          </Button>
        </Box>
      </Box>

      {/* Settings Accordion - collapsed by default */}
      <Accordion disableGutters defaultExpanded={false} sx={{ flexShrink: 0, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SettingsIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Session Settings</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <SessionSettingsPanel sessionId={sessionId} isOwner={isOwner} settings={store.votingSettings} />
        </AccordionDetails>
      </Accordion>

      {/* Leave button */}
      <Box sx={{ p: 2, mt: 'auto', flexShrink: 0 }}>
        <Button
          fullWidth
          variant="text"
          color="error"
          size="small"
          startIcon={<LeaveIcon />}
          onClick={() => setLeaveConfirmOpen(true)}
          sx={{ justifyContent: 'flex-start' }}
        >
          Leave Session
        </Button>
      </Box>
    </Box>
  )

  const renderChatPanel = () => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {/* Chat header - desktop only (mobile has it integrated in the flow) */}
      {!isMobile && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <ChatIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Group Chat
          </Typography>
        </Box>
      )}

      {conversationId ? (
        <>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <MessageThread
              conversationId={conversationId}
              typingUsers={Array.from(typingUsers.entries()).map(([userId, { userName }]) => ({ userId, userName }))}
            />
          </Box>
          <MessageInput
            onSend={handleSendMessage}
            onTyping={handleTyping}
            onImageUpload={handleImageUpload}
            disabled={sendMessageMutation.isPending}
            placeholder="Chat with your group..."
          />
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            color: 'text.disabled',
          }}
        >
          <ChatIcon sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography variant="body2" color="text.secondary">
            Chat will be available once the session has a conversation
          </Typography>
        </Box>
      )}
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 1, md: 2 },
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
            {adventureTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Chip
              label="Lobby"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
            <Typography variant="caption" color="text.secondary" noWrap>
              Waiting for players to ready up
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {/* Leave button - mobile only (desktop has it in left panel) */}
          {isMobile && (
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={() => setLeaveConfirmOpen(true)}
              sx={{ minWidth: 0, px: 1 }}
            >
              <LeaveIcon fontSize="small" />
            </Button>
          )}
          {isOwner && (
            <Tooltip
              title={readyCount < totalCount ? `Waiting for ${totalCount - readyCount} player(s) to ready up` : ''}
              disableHoverListener={readyCount >= totalCount}
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={startSession.isPending ? <CircularProgress size={16} color="inherit" /> : <StartIcon />}
                  onClick={handleStartSession}
                  disabled={startSession.isPending || readyCount < totalCount}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Start
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {store.proximityWarning && (
        <Alert severity="warning" sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
          {store.proximityWarning}
        </Alert>
      )}
      {startErrors.length > 0 && (
        <Alert severity="error" sx={{ mx: 2, mt: 1, flexShrink: 0 }} onClose={() => setStartErrors([])}>
          {startErrors.map((e, i) => (
            <Typography key={i} variant="body2">{e}</Typography>
          ))}
        </Alert>
      )}

      {/* Mobile layout: compact strip + chat fills remaining space */}
      {isMobile ? (
        <>
          {renderMobileParticipants()}
          {renderChatPanel()}
        </>
      ) : (
        /* Desktop layout: two columns */
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {renderDesktopLeftPanel()}
          {renderChatPanel()}
        </Box>
      )}

      {/* Settings dialog (mobile) */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          Session Settings
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <SessionSettingsPanel sessionId={sessionId} isOwner={isOwner} settings={store.votingSettings} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Invite panel */}
      <SessionInvitePanel
        open={invitePanelOpen}
        onClose={() => setInvitePanelOpen(false)}
        sessionId={sessionId}
      />

      {/* Leave confirmation */}
      <Dialog open={leaveConfirmOpen} onClose={() => setLeaveConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Leave Session?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to leave this session? You can rejoin later if the session is still active.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveConfirmOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleLeaveSession} color="error" variant="contained">Leave</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
